import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";

import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React from "react";
import { css } from "styled-components";

interface Fields {
    breadcrumbLabel: string;
    heading: string;
    description: string;
    backgroundImage: string;
}

type Props = WidgetProps & { fields: Fields };

export interface BranchHeroStyles {
    wrapper?: InjectableCss;
    textColumn?: InjectableCss;
    breadcrumb?: InjectableCss;
    heading?: InjectableCss;
    description?: InjectableCss;
    imageColumn?: InjectableCss;
}

export const branchHeroStyles: BranchHeroStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            gap: 64px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    // xs: text on top, image below (DOM order is image first, so reverse the column)
                    css`
                        flex-direction: column-reverse;
                        align-items: stretch;
                        gap: 16px;
                        padding: 0 16px 24px;
                    `,
                    // sm
                    css`
                        flex-direction: column-reverse;
                        align-items: stretch;
                        gap: 24px;
                        padding: 0 24px 24px;
                    `,
                    // md: keep the row, let the image shrink
                    css`
                        gap: 24px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    textColumn: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            align-self: center;
            padding: 0px 64px 0px 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 16px 0 0;
                    `,
                    css`
                        padding: 24px 0 0;
                    `,
                    css`
                        padding: 0 16px 0 0;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    breadcrumb: {
        css: css`
            font-size: 14px;
            line-height: 16px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
           color: ${getColor("primary")};
            margin-bottom: 8px;
        `,
    },
    heading: {
        css: css`
            font-size: 40px;
            line-height: 48px;
            font-weight: ${TYPOGRAPHY.weight.black};
            color: ${COLORS.black};
            margin: 0 0 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 32px;
                        line-height: 40px;
                        margin-bottom: 12px;
                    `,
                    css`
                        font-size: 36px;
                        line-height: 44px;
                    `,
                    css`
                        font-size: 32px;
                        line-height: 40px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    description: {
        css: css`
            font-size: 18px;
            line-height: 24px;
            color:${COLORS.black};
        `,
    },
    imageColumn: {
        css: css`
            /* desktop: 499 x 308 */
            flex: 0 0 499px;
            width: 499px;
            height: 308px;
            overflow: hidden;
            img {
                display: block;
                width: 100%;
                height: 100%;
                object-fit: cover;
                object-position: center;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex: none;
                        width: 100%;
                        height: 240px;
                    `,
                    css`
                        flex: none;
                        width: 100%;
                        height: 308px;
                    `,
                    css`
                        flex: 0 0 45%;
                        width: 45%;
                        height: 280px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};

const BranchHero = ({ fields }: Props) => {
    const styles = branchHeroStyles;
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.imageColumn}>
                <Img src={fields.backgroundImage} altText={fields.heading} />
            </StyledWrapper>
            <StyledWrapper {...styles.textColumn}>
                <Typography as="p" {...styles.breadcrumb}>
                    {fields.breadcrumbLabel}
                </Typography>
                <Typography as="h1" {...styles.heading}>
                    {fields.heading}
                </Typography>
                <Typography as="p" {...styles.description}>
                    {fields.description}
                </Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BranchHero,
    definition: {
        group: "Common",
        displayName: "CEN Branch Hero",
        fieldDefinitions: [
            { name: "breadcrumbLabel", displayName: "Breadcrumb label", editorTemplate: "TextField", defaultValue: "Branch Locations", fieldType: "Translatable" },
            { name: "heading", displayName: "Heading", editorTemplate: "TextField", defaultValue: "Real People. Regional Support.", fieldType: "Translatable" },
            { name: "description", displayName: "Description", editorTemplate: "RichTextField", defaultValue: "", fieldType: "Translatable" },
            { name: "backgroundImage", displayName: "Image", editorTemplate: "ImagePickerField", defaultValue: "", fieldType: "General" },
        ],
    },
};

export default widgetModule;